import XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";

/**
 * Export Day Book data to Excel
 * @param {Object} params
 * @param {Array} params.dayBookData - flat data array
 * @param {string} params.fromDate - report start date (string)
 * @param {string} params.toDate - report end date (string)
 * @param {string} params.societyName - society / company name
 */
export const exportDayBookExcel = ({ dayBookData, fromDate, toDate, schoolName }) => {
  const wb = XLSX.utils.book_new();
  const wsData = [];

  const formatDate = (isoDate) => {
    if (!isoDate) return "";
    const [year, month, day] = isoDate.split("-");
    return `${day}/${month}/${year}`;
  };

  const formatCurrency = (amount) => {
    const num = Number(amount) || 0;
    return new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  // --- Add Title Rows ---
  wsData.push([schoolName]);
  wsData.push([]);
  wsData.push([`Day Book Report For the Period: ${formatDate(fromDate)} To ${formatDate(toDate)}`]);
  wsData.push([]);
  
  // --- Group data by date ---
  const groupedData = {};
  dayBookData.forEach((entry) => {
    if (!groupedData[entry.vchr_date]) groupedData[entry.vchr_date] = [];
    groupedData[entry.vchr_date].push(entry);
  });

  let grandTotalDr = 0;
  let grandTotalCr = 0;

  // --- For each Date Group ---
  Object.entries(groupedData).forEach(([date, entries]) => {
    wsData.push([`Date: ${date}`]);
    wsData.push(["Vchr No", "Account", "Narration", "Debit Amount", "Credit Amount"]);

    let dateTotalDr = 0;
    let dateTotalCr = 0;

    entries.forEach((e) => {
      const drAmt = Number(e.dr_amt) || 0;
      const crAmt = Number(e.cr_amt) || 0;

      wsData.push([
        e.vchr_no || "",
        e.cb_acct_name || "",
        e.narration || "",
        formatCurrency(drAmt),
        formatCurrency(crAmt),
      ]);

      dateTotalDr += drAmt;
      dateTotalCr += crAmt;
    });

    // --- Subtotal Row ---
    wsData.push([
      "",
      "",
      `Total for ${date}`,
      formatCurrency(dateTotalDr),
      formatCurrency(dateTotalCr),
    ]);

    wsData.push([]); // spacing row

    grandTotalDr += dateTotalDr;
    grandTotalCr += dateTotalCr;
  });

  // --- Grand Total ---
  wsData.push([
    "",
    "",
    "Grand Total",
    formatCurrency(grandTotalDr),
    formatCurrency(grandTotalCr),
  ]);

  // --- Convert to Worksheet ---
  const ws = XLSX.utils.aoa_to_sheet(wsData);
  const range = XLSX.utils.decode_range(ws["!ref"]);

  // --- Styling ---
  for (let R = range.s.r; R <= range.e.r; ++R) {
    const row = wsData[R];

    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cell_ref = XLSX.utils.encode_cell({ r: R, c: C });
      const cell = ws[cell_ref];
      if (!cell) continue;

      // Center-align top title
      if (R === 0) {
        cell.s = {
          font: { bold: true, sz: 14 },
          alignment: { horizontal: "center" },
        };
        ws["!merges"] = ws["!merges"] || [];
        ws["!merges"].push({ s: { r: R, c: 0 }, e: { r: R, c: 4 } });
      }

      // Report subtitle (range)
      if (R === 2) {
        cell.s = {
          font: { bold: true, sz: 12 },
          alignment: { horizontal: "center" },
        };
        ws["!merges"] = ws["!merges"] || [];
        ws["!merges"].push({ s: { r: R, c: 0 }, e: { r: R, c: 4 } });
      }

      // Date header row (e.g. “Date: 2025-11-09”)
      if (row && row.length === 1 && row[0]?.startsWith("Date:")) {
        cell.s = {
          font: { bold: true, sz: 12 },
        //  fill: { fgColor: { rgb: "DCE6F1" } },
        };
        ws["!merges"] = ws["!merges"] || [];
        ws["!merges"].push({ s: { r: R, c: 0 }, e: { r: R, c: 4 } });
      }

      // Table header (Vchr No, Account, etc.)
      if (row && row[0] === "Vchr No") {
        cell.s = {
          font: { bold: true,}, //color: { rgb: "FFFFFF" } },
          alignment: { horizontal: "center" },
         // fill: { fgColor: { rgb: "2980B9" } },
         fill: { fgColor: { rgb: "E6E6E6" } },
         
        };
      }

      // Totals (subtotal or grand total)
      if (row && (row[2]?.startsWith("Total for") || row[2] === "Grand Total")) {
        cell.s = {
          font: { bold: true },
         // fill: { fgColor: { rgb: "E6E6E6" } },
        };
      }

      // Align numbers to right
      if ([3, 4].includes(C)) {
        cell.s = {
          ...(cell.s || {}),
          alignment: { horizontal: "right" },
        };
      }
    }
  }

  // --- Column widths ---
  ws["!cols"] = [
    { wch: 10 }, // Vchr No
    { wch: 30 }, // Account
    { wch: 50 }, // Narration
    { wch: 18 }, // Debit Amount
    { wch: 18 }, // Credit Amount
  ];

  // --- Add sheet & save file ---
  XLSX.utils.book_append_sheet(wb, ws, "Day Book Report");

  const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  saveAs(
    new Blob([wbout], { type: "application/octet-stream" }),
    `DayBook_Report_${formatDate(fromDate)}_To_${formatDate(toDate)}.xlsx`
  );
};
